<template>
  <div class="modal-wrap">
    <div class="edit-form-wrap">
      <span class="material-icons" @click="$emit('close-modal')">
        close
      </span>
      <h2>{{template? "Edit Template" : "Add Template"}}</h2>
      <div class="name">
        <label for="name">Template Name:</label>
        <input type="text" id="name" ref="file" v-model.trim="templateName">
      </div>
      <div class="img-box-buttons" v-if="image">
        <label class="change-image" for="file">Change Image</label>
        <button class="cancel-button reset" @click="resetCoords">Reset clickable items</button>
        <button class="cancel-button reset" @click="clearCoords">Clear clickable items</button>
      </div>
      <div v-if="isDataReady" :class="image? 'img-box-has-image' : 'img-box'">
        <input type="file" id="file" @change="getFile">
        <label for="file" v-if="!image">Click to upload a file...</label>
        <clickable-area-select
          v-else
          :image="image"
          :selected="newCoords"
          @save="setNewCoords"
        ></clickable-area-select>
      </div>
      <div class="buttons">
        <button class="confirm-button" @click="template? editTemplate() : addTemplate()">{{template? "Edit Template" : "Add Template"}}</button>
        <button class="cancel-button" @click="$emit('close-modal')">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import AdminMixin from '../../../views/admin-pages/adminMixin'
import ClickableAreaSelect from './clickableAreaSelect';
export default {
  mixins: [
    AdminMixin
  ],
  components: {
    ClickableAreaSelect
  },
  props: {
    template: Object
  },
  data () {
    return {
      image: '',
      oldImage: '',
      isDataReady: false,
      templateName: this.template ? this.template.tmp_name : '',
      oldTemplateName: this.template ? this.template.tmp_name : '',
      oldCoords: this.template? this.parseCoords() : [],
      newCoords: this.template? this.parseCoords() : []
    }
  },
  mounted () {
    this.checkForTemplateImage();
  },
  methods: {
    parseCoords () {
      let coordsArray = [];
      let templateCoords = JSON.parse(this.template.tmp_elems);
      templateCoords = typeof templateCoords === "object" ? templateCoords : JSON.parse(templateCoords);
      for (let i = 0; i < [templateCoords][0].length; i++) {
        coordsArray.push([templateCoords][0][i])
      }
      return coordsArray;
    },
    checkForTemplateImage () {
      if (this.template) this.getTemplateImage()
      else this.isDataReady = true;
    },
    async getTemplateImage () {
      this.startLoading()
      try {
        const res = await api.getTemplateImage({
          tmp_id: this.template.tmp_id
        });
        this.image = res.data;
        this.oldImage = res.data;
        this.isDataReady = true;
      } catch (error) {
        this.handleError(error)
      }
      this.stopLoading();
    },
    async editTemplate () {
      if (this.hasErrors()) {
        return;
      }

      let data = {};
      let isImageChanged = false;

      if (this.templateName !== this.oldTemplateName) {
        data.tmp_name = this.templateName;
      }
      if (!this.areArraysEqual(this.oldCoords, this.newCoords)) {
        data.tmp_elems = JSON.stringify(this.newCoords);
      }
      if (this.oldImage !== this.image) {
        this.addTemplateImage(this.template.tmp_id);
        isImageChanged = true;
      }
      
      if (Object.keys(data).length === 0 && data.constructor === Object && !isImageChanged) {
        this.EventBus.$emit('error', new Error ('No changes were made.'))
        return;
      } else if (Object.keys(data).length === 0 && data.constructor === Object && isImageChanged) {
        this.emitGetDataCloseModal();
        this.showSuccessMessage(this.$t(`templates.editSuccess`))
        return;
      }

      try {
        this.startLoading();
        await api.editTemplate({
          tmp_id: this.template.tmp_id,
          data
        });
        this.emitGetDataCloseModal();
        this.showSuccessMessage(this.$t(`templates.editSuccess`))
      } catch (error) {
        this.handleError(error)
      }
      this.stopLoading();
    },
    areArraysEqual (array1, array2) {
      // if any array is a falsy value, return
      if (!array1 || !array2) {
        return false;
      }
      // compare lengths - can save a lot of time 
      if (array1.length != array2.length) {
        return false;
      }

      for (let i = 0; i < array1.length; i++) {
        for (let coordinate in array1[i].coords) {
          if (array1[i].coords[coordinate] !== array2[i].coords[coordinate]) return false
        }
      }    

      return true;
    },
    emitGetDataCloseModal () {
      this.$emit('get-templates');
      this.$emit('close-modal');
    },
    async addTemplate () {
      if (this.hasErrors()) {
        return;
      }
      try {
        this.startLoading();
        const res = await api.addTemplate({
          tmp_name: this.templateName,
          tmp_elems: this.newCoords
        });

        const newTemplateId = res.data.tmp_id.id

        this.addTemplateImage(newTemplateId);

        this.emitGetDataCloseModal();
        this.showSuccessMessage(this.$t(`templates.addSuccess`))
      } catch (error) {
        this.handleError(error);
      }
      this.stopLoading();
    },
    async addTemplateImage (tmpId) {
      try {
        this.startLoading();
        let formData = new FormData();
        formData.append('tmp_id', tmpId);
        formData.append('tmp_image', this.image);
        await api.addTemplateImage(formData); 
      } catch (error) {
        this.handleError(error);
      }
      this.stopLoading();
    },
    hasErrors () {
      if (this.templateName.length === 0) {
        this.EventBus.$emit('error', new Error ('Please fill the template name.'))
        return true
      }

      if (this.templateName.length > 255) {
        this.EventBus.$emit('error', new Error ('Please use a shorter name.'))
        return true
      }

      if (!this.image) {
        this.EventBus.$emit('error', new Error ('Please upload an image.'))
        return true
      }

      if (this.newCoords.length === 0) {
        this.EventBus.$emit('error', new Error ('Template should have at least one clickable element.'))
        return true
      }

      return false;
    },
    getFile (e) {
      let tgt = e.target || window.event.srcElement,
      files = tgt.files;
      // FileReader support
      if (FileReader && files && files.length) {
        let fr = new FileReader();
        fr.onload = () => {
          this.image = fr.result;
        };
        fr.readAsDataURL(files[0]);
      }
    },
    setNewCoords (array) {
      this.newCoords = this.copyCoordsArray(array.data);
    },
    resetCoords () {
      this.newCoords = this.copyCoordsArray(this.oldCoords);
    },
    clearCoords () {
      this.newCoords = [];
    },
    //Work only for [{coords:{}}, {coords:{}}] format
    copyCoordsArray (arr) {
      let copyArr = [];
      for (let i = 0; i < arr.length; i++) {
        let obj = {};
        obj.coords = {...arr[i].coords};
        copyArr.push(obj);
      }
      return copyArr;
    }
  },
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}
.modal-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  position:fixed;
  padding:0;
  margin:0;

  top:0;
  left:0;

  width: 100%;
  height: 100%;
  background:rgba(0, 0, 0, 0.7);
  z-index: 20;
}
.edit-form-wrap {
  position: relative;
  width: 60%;
  min-height: 70vh;
  max-height: 90vh;
  /* overflow: auto; */
  background: #FFF;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-radius: 20px;
  padding: 0 20px;
}
.material-icons {
  position: absolute;
  top: 5px;
  right: 10px;
  cursor: pointer;
}
.img-box {
  position: relative;
  min-height: 300px;
  min-width: 220px;
  width: 60%;
  border: 3px solid gray;
  border-radius: 10px;
}
.img-box-has-image {
  position: relative;
  min-width: auto;
  min-height: auto;
  overflow: auto;
}
.img-box label {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}
.name {
  width: 60%;
  margin: 20px 0px;
}
.name input {
  width: 200px;
}
#file {
  display: none;
}
.buttons {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 20px 0;
}
button {
  cursor: pointer;
  white-space: nowrap;
  border: none;
  padding: 8px 23px;
  border-radius: 100px;
  font-size: 13px;
  margin: 5px 10px;
  outline: none;
}
.confirm-button {
  background-color: var(--button-color-blue);
  color: white;
}
.cancel-button {
  background: #e5e5e5;
}
.cancel-button:hover {
  background: #e5e5e5;
}
.img-box-buttons {
  display: flex;
  flex-direction: row;
  width: 100%;
}
.cancel-button.reset {
  border: 2px solid #e5e5e5; 
  background: #e5e5e5; 
  text-align: center;
}
.change-image {
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  padding: 8px 23px;
  border-radius: 100px;
  font-size: 13px;
  margin: 5px 10px;
  outline: none;
  border: 2px solid #e5e5e5; 
  background: #e5e5e5; 
}
</style>